import { SEO } from "@/components/SEO";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Award,
  Clock,
  BookOpen,
  ArrowLeft,
  Target,
  GraduationCap,
  Video,
  Zap,
  CheckCircle2,
  CalendarIcon,
  Layers,
  PlayCircle,
  FileText,
  Infinity as InfinityIcon,
} from "lucide-react";

import { useProgramBySlug, usePublicCohorts, usePublicPrograms } from "@/hooks/usePrograms";
import { useSelfPacedRouteOutline } from "@/hooks/useSelfPacedRouteOutline";
import { RouteInterestButton } from "@/components/public/RouteInterestButton";
import { SelfPacedRouteCta } from "@/components/public/SelfPacedRouteCta";
import { useMySelfPacedEnrollments, useSelfPacedProgress } from "@/hooks/useSelfPacedRoutes";
import { routeImage } from "@/lib/routeImage";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const levelLabels: Record<number, string> = {
  1: "Básico",
  2: "Intermedio",
  3: "Avanzado",
  4: "Experto",
};

const contentTypeIcon = (type?: string | null) => {
  if (type === "video") return PlayCircle;
  if (type === "reading" || type === "pdf") return FileText;
  return BookOpen;
};

const CertificationDetailPage = () => {
  const { certificationId } = useParams();
  const navigate = useNavigate();

  const { data: certification, isLoading } = useProgramBySlug(certificationId);
  const { data: cohorts } = usePublicCohorts(certification?.id);
  const { data: allPrograms } = usePublicPrograms();
  const { data: chapters } = useSelfPacedRouteOutline(certification?.id, !!certification?.id);

  const openCohorts = (cohorts || []).filter((c) => c.enrollment_open && !c.is_perpetual);
  const isSelfPaced = (certification as any)?.modality === "self_paced";
  const { data: myRoutes } = useMySelfPacedEnrollments();
  const enrolledSelfPaced = !!certification && (myRoutes || []).includes(certification.id);
  const { data: progressMap } = useSelfPacedProgress(
    isSelfPaced && enrolledSelfPaced && certification ? [certification.id] : [],
  );

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-64 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
      </div>
    );
  }

  if (!certification) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <h1 className="text-2xl font-bold text-foreground mb-4">Ruta no encontrada</h1>
        <Button onClick={() => navigate("/rutas")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a rutas
        </Button>
      </div>
    );
  }

  const categoryLabel = certification.category === "specialized" ? "Ruta Especializada" : "Ruta Principal";
  const levelLabel = certification.level ? levelLabels[certification.level] || `Nivel ${certification.level}` : null;
  const chapterList = chapters ?? [];
  const totalContents = chapterList.reduce((acc, ch) => acc + ch.contents.length, 0);
  const progress = progressMap?.[certification.id];
  const skills = (certification.skills as string[] | null) ?? [];

  const relatedCerts = (allPrograms || [])
    .filter(
      (c) => c.id !== certification.id && ((c as any).modality === "self_paced") === isSelfPaced,
    )
    .slice(0, 3);

  const courseJsonLd = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: certification.name,
    description: certification.description,
    provider: {
      "@type": "Organization",
      name: "LÍDER IA",
      sameAs: "https://lideria.org",
    },
  };

  const MainCta = ({ className }: { className?: string }) =>
    isSelfPaced ? (
      <SelfPacedRouteCta
        programId={certification.id}
        programName={certification.name}
        description={certification.description}
        enrolled={enrolledSelfPaced}
        progress={progress}
        size="lg"
        className={className}
      />
    ) : (
      <RouteInterestButton
        programId={certification.id}
        programName={certification.name}
        size="lg"
        className={className}
      />
    );

  return (
    <div className="min-h-screen pb-16">
      <SEO
        title={`${certification.name} — ${isSelfPaced ? "Ruta de autoaprendizaje" : "Ruta con acompañamiento"} | LÍDER IA`}
        description={certification.description || `${categoryLabel} en IA: ${certification.name}.`}
        path={`/rutas/${certificationId}`}
        jsonLd={courseJsonLd}
      />

      <div className="max-w-6xl mx-auto px-6 pt-6">
        <Button variant="ghost" onClick={() => navigate("/rutas")} className="gap-2 hover:bg-muted/50">
          <ArrowLeft className="w-4 h-4" />
          Todas las rutas
        </Button>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-6">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div className="rounded-2xl overflow-hidden border border-border/50 bg-muted aspect-video">
            <img
              src={routeImage(certification as any)}
              alt={certification.name}
              width={1024}
              height={576}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="gap-1">
                {isSelfPaced ? (
                  <>
                    <InfinityIcon className="w-3 h-3" /> Autoaprendizaje
                  </>
                ) : (
                  <>
                    <Video className="w-3 h-3" /> Con acompañamiento
                  </>
                )}
              </Badge>
              <Badge variant="outline" className="border-primary/30 text-primary">
                {categoryLabel}
              </Badge>
              {levelLabel && <Badge variant="secondary">{levelLabel}</Badge>}
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-foreground leading-tight">
              {certification.name}
            </h1>

            {certification.description && (
              <p className="text-base text-muted-foreground leading-relaxed line-clamp-[10]">{certification.description}</p>
            )}

            <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
              {certification.duration && (
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4 text-primary" /> {certification.duration}
                </span>
              )}
              {chapterList.length > 0 && (
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Layers className="w-4 h-4 text-primary" /> {chapterList.length} capítulos
                </span>
              )}
              {totalContents > 0 && (
                <span className="flex items-center gap-2 text-muted-foreground">
                  <BookOpen className="w-4 h-4 text-primary" /> {totalContents} contenidos
                </span>
              )}
            </div>

            {isSelfPaced && enrolledSelfPaced && typeof progress === "number" && (
              <div className="space-y-1.5 max-w-sm">
                <Progress value={progress} className="h-2" />
                <p className="text-xs text-muted-foreground">{progress}% completado</p>
              </div>
            )}

            <div className="pt-1">
              <MainCta className="gap-2 text-base px-8" />
            </div>
          </div>
        </div>
      </section>

      {/* Bloque según tipo */}
      <section className="max-w-6xl mx-auto px-6 pt-12">
        {isSelfPaced ? (
          <Card className="border-border/50">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <h2 className="font-semibold text-foreground">A tu ritmo, incluida en tu plan</h2>
                  <p className="text-sm text-muted-foreground">
                    Avanza cuando quieras: sin fechas de inicio ni sesiones en vivo.
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                {["Acceso mientras tu plan esté activo", "Contenido siempre disponible", "Tu progreso queda guardado"].map(
                  (item) => (
                    <div key={item} className="flex items-start gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-secondary mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ),
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border/50">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-semibold text-foreground">Ruta con acompañamiento</h2>
                  <p className="text-sm text-muted-foreground">
                    Sesiones en vivo, mentoría y certificación al completar la ruta.
                  </p>
                </div>
              </div>

              {openCohorts.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  {openCohorts.map((cohort) => (
                    <div key={cohort.id} className="rounded-xl border border-border/50 bg-muted/40 p-4 space-y-2">
                      <p className="text-sm font-semibold text-foreground">{cohort.name}</p>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        {cohort.start_date && (
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="w-4 h-4" />
                            Inicia {format(new Date(cohort.start_date), "d 'de' MMMM, yyyy", { locale: es })}
                          </span>
                        )}
                        {cohort.price != null && (
                          <Badge variant="secondary">
                            {cohort.currency || "USD"} ${cohort.price}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  La próxima cohorte está por programar. Déjanos tus datos y te avisamos cuando abra la inscripción.
                </p>
              )}

              <RouteInterestButton
                programId={certification.id}
                programName={certification.name}
                className="w-full sm:w-auto"
              />
            </CardContent>
          </Card>
        )}
      </section>

      {/* Temario */}
      {chapterList.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pt-12">
          <h2 className="text-2xl font-bold text-foreground mb-1">Temario</h2>
          <p className="text-sm text-muted-foreground mb-6">
            {chapterList.length} capítulos · {totalContents} contenidos
          </p>

          <Accordion type="multiple" className="space-y-3">
            {chapterList.map((ch, idx) => (
              <AccordionItem
                key={ch.id}
                value={ch.id}
                className="border border-border/50 rounded-xl bg-card px-4"
              >
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3 text-left w-full pr-3">
                    <div className="w-8 h-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                      {idx + 1}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{ch.name}</h3>
                      {ch.week_number != null && !/semana/i.test(ch.name) && (
                        <p className="text-xs text-muted-foreground">Semana {ch.week_number}</p>
                      )}
                    </div>
                    <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
                      {ch.contents.length} {ch.contents.length === 1 ? "contenido" : "contenidos"}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  {ch.contents.length > 0 ? (
                    <ul className="space-y-1.5 pl-11">
                      {ch.contents.map((c) => {
                        const Icon = contentTypeIcon(c.content_type);
                        return (
                          <li key={c.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Icon className="w-4 h-4 shrink-0 text-muted-foreground/70" />
                            <span className="text-foreground/90">{c.name}</span>
                            {c.duration && <span className="text-xs">· {c.duration}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground pl-11">Contenido en preparación.</p>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* Competencias */}
      {skills.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pt-12">
          <h2 className="text-2xl font-bold text-foreground mb-6">Lo que vas a dominar</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {skills.map((skill) => (
              <Card key={skill} className="border-border/50">
                <CardContent className="p-5 flex items-start gap-3">
                  <Target className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <h3 className="font-medium text-foreground text-sm leading-snug">{skill}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Otras rutas */}
      {relatedCerts.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pt-12">
          <h2 className="text-2xl font-bold text-foreground mb-6">
            {isSelfPaced ? "Otras rutas de autoaprendizaje" : "Otras rutas con acompañamiento"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {relatedCerts.map((cert) => (
              <Card
                key={cert.id}
                className="group cursor-pointer border-border/50 hover:border-primary/30 hover:shadow-lg transition-all overflow-hidden"
                onClick={() => navigate(`/rutas/${cert.slug}`)}
              >
                <div className="aspect-video overflow-hidden bg-muted">
                  <img
                    src={routeImage(cert as any)}
                    alt={cert.name}
                    loading="lazy"
                    width={1024}
                    height={576}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <CardContent className="p-4 space-y-2">
                  <h3 className="font-semibold text-foreground text-sm leading-tight line-clamp-2">{cert.name}</h3>
                  <p className="text-xs text-muted-foreground">
                    {[
                      cert.level ? levelLabels[cert.level] || `Nivel ${cert.level}` : null,
                      cert.duration,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/rutas/${cert.slug}`);
                    }}
                  >
                    <Award className="w-4 h-4 mr-1" /> Ver info
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default CertificationDetailPage;
